import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export type PanelVariant = 'default' | 'quiet' | 'flush';

interface PanelProps {
  /** Quiet panels carry reference/footnote material; they must not compete. */
  variant?: PanelVariant;
  title?: React.ReactNode;
  /** Optional glyph shown before the title. */
  icon?: IconDefinition;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  id?: string;
}

/**
 * The neutral surface primitive that replaces `<Card className="shadow">`.
 *
 * `default` is primary content, `quiet` is secondary material (retention,
 * how-to), `flush` drops the shadow for content that hosts its own wrapper.
 */
const Panel: React.FC<PanelProps> = ({
  variant = 'default',
  title,
  icon,
  subtitle,
  actions,
  children,
  className = '',
  bodyClassName = '',
  id,
}) => {
  const hasHead = Boolean(title || subtitle || actions);
  return (
    <section id={id} className={`surface-panel surface-panel--${variant} ${className}`.trim()}>
      {hasHead && (
        <header className="surface-panel__head">
          <div className="surface-panel__heading">
            {title && (
              <h2 className="surface-panel__title">
                {icon && <FontAwesomeIcon icon={icon} className="surface-panel__icon" aria-hidden="true" />}
                <span>{title}</span>
              </h2>
            )}
            {subtitle && <p className="surface-panel__subtitle">{subtitle}</p>}
          </div>
          {actions && <div className="surface-panel__actions">{actions}</div>}
        </header>
      )}
      <div className={`surface-panel__body ${bodyClassName}`.trim()}>{children}</div>
    </section>
  );
};

export default Panel;
